/**
 * NEX-1164 — the per-field rules of a depósito's account data, mirrored from
 * `nexvia-api-web/src/modules/storage/domain/storage-profile-fields.ts` (the rules both
 * `POST /auth/register-storage` and `PATCH /storage/me` apply). Client-side only, for immediate
 * feedback: the API's codes (mapped in `storage-api.ts`'s `describeStorageProfileError`) still decide.
 * If that file changes a rule, this one changes with it.
 */

/** 12 digits after trimming, no DGI check digit — the API's own `[ASSUMED]` rule, same as the
 *  carrier registration's `rutNumber`. A check-digit rule would land there first. */
const RUT_PATTERN = /^\d{12}$/;

/** The API's `EMAIL_PATTERN` (same shape as the guest flow's). */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The API's column widths (`business_name` 200, `users.phone` 30, `contact_email` 320,
 *  `contact_phone` 30): used as `maxLength` on the inputs so `VALIDATION_FIELD_TOO_LONG` can't happen. */
export const FIELD_MAX_LENGTH = { razonSocial: 200, phone: 30, contactEmail: 320, contactPhone: 30 } as const;

export function isValidRut(rut: string): boolean {
  return RUT_PATTERN.test(rut.trim());
}

export function isValidContactEmail(email: string): boolean {
  return EMAIL_PATTERN.test(email.trim());
}

export interface RegistrationInput {
  razonSocial: string;
  rut: string;
  phone: string;
  /** Optional: blank means "use my login email" (the API's default). */
  contactEmail: string;
}

export type RegistrationErrors = Partial<Record<keyof RegistrationInput, string>>;

/** The registration form's checks — the same four the API runs on a first `register-storage` call. */
export function validateRegistration(input: RegistrationInput): RegistrationErrors {
  const errors: RegistrationErrors = {};
  if (!input.razonSocial.trim()) errors.razonSocial = 'Ingresá la razón social de tu empresa.';
  if (!isValidRut(input.rut)) errors.rut = 'El RUT tiene que tener exactamente 12 dígitos.';
  if (!input.phone.trim()) errors.phone = 'Ingresá un teléfono de contacto.';
  if (input.contactEmail.trim() && !isValidContactEmail(input.contactEmail)) {
    errors.contactEmail = 'Ese correo no es válido.';
  }
  return errors;
}
